import { AxiosResponse } from 'axios';
import Archivable, { ArchivedUpdatedEvent } from './shared/archivable';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { Fields } from './shared/fields';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface MessageResponse {
  message: string;
}

export interface PresentationProperties {
  name: string;
  data: Fields;
  tags?: string[];
}

export interface GetPresentationsResponse {
  presentations: Presentation[];
  continuationToken?: string;
}

export interface GetPresentationsRequest {
  limit?: number;
  continuationToken?: string;
  allowArchived?: boolean;
}

export interface Presentation extends Entity, PresentationProperties, Archivable {}

export type UpdatePresentation = Partial<PresentationProperties>;
export type CreatePresentation = PresentationProperties;

export type PERMISSIONS =
  | typeof DELETE
  | typeof GET_ALL
  | typeof CREATE
  | typeof UPDATE
  | typeof SET_ARCHIVED
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_UPDATED
  | typeof GET_EVENTS_DELETED
  | typeof GET_EVENTS_ARCHIVED_UPDATED;

export const CREATE = 'PRESENTATION:CREATE';
export const DELETE = 'PRESENTATION:DELETE';
export const UPDATE = 'PRESENTATION:UPDATE';
export const GET_ALL = 'PRESENTATION:GET_ALL';
export const SET_ARCHIVED = 'PRESENTATION:SET_ARCHIVED';
export const GET_EVENTS_ANY = 'PRESENTATION:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'PRESENTATION:GET_EVENTS:CREATED';
export const GET_EVENTS_DELETED = 'PRESENTATION:GET_EVENTS:DELETED';
export const GET_EVENTS_UPDATED = 'PRESENTATION:GET_EVENTS:UPDATED';
export const GET_EVENTS_ARCHIVED_UPDATED = 'PRESENTATION:GET_EVENTS:ARCHIVED_UPDATED';

export const PERMISSION_NAMES = {
  CREATE,
  DELETE,
  UPDATE,
  GET_ALL,
  SET_ARCHIVED,
  GET_EVENTS_ANY,
  GET_EVENTS_CREATED,
  GET_EVENTS_DELETED,
  GET_EVENTS_UPDATED,
  GET_EVENTS_ARCHIVED_UPDATED,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: Presentation;
}

export interface UpdatedEvent extends Event {
  type: 'UPDATED';
  data: UpdatePresentation;
}

export interface DeletedEvent extends Event {
  type: 'DELETED';
}

export type PresentationEvents = CreatedEvent | DeletedEvent | UpdatedEvent | ArchivedUpdatedEvent;

export class PresentationClient extends ServiceClientWithEventstore<PresentationEvents> {
  public async get(id: string): Promise<AxiosResponse<Presentation>> {
    return await this.httpClient.get(`/${id}`, this.requestConfig);
  }

  public async getAll(input: GetPresentationsRequest = {}): Promise<AxiosResponse<GetPresentationsResponse>> {
    return await this.httpClient.get('/', {
      params: input,
      ...this.requestConfig,
    });
  }

  public async create(createPresentation: CreatePresentation): Promise<AxiosResponse<Presentation>> {
    return await this.httpClient.post('/', createPresentation, this.requestConfig);
  }

  public async update(id: string, updatePresentation: UpdatePresentation): Promise<AxiosResponse<Presentation>> {
    return await this.httpClient.patch(`/${id}`, updatePresentation, this.requestConfig);
  }

  public async delete(id: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/${id}`, this.requestConfig);
  }

  public async setArchived(id: string, archived: boolean): Promise<AxiosResponse<Presentation>> {
    return await this.httpClient.put(`/${id}/archived`, { archived }, this.requestConfig);
  }
}
